import { useEffect, useMemo, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Asset, useListAssetsQuery } from "features/trading/accounts";
import { SelectedAsset } from "helpers/types";
import { useDebounce } from "use-debounce";

const MIN_SEARCH_LENGTH = 2;
const ASSET_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 250;
const DEFAULT_PLACEHOLDER = "Search by symbol or name...";
const DEFAULT_LABEL = "Asset";

export const getAssetLabel = (asset: Pick<Asset, "symbol" | "name">) =>
  `${asset.symbol} - ${asset.name}`;

const toSelectedAsset = ({ id, symbol, name }: Asset): SelectedAsset => ({
  id,
  symbol,
  name,
});

const getNoOptionsText = (trimmedSearch: string, hasHeldContext: boolean) => {
  if (trimmedSearch.length >= MIN_SEARCH_LENGTH) return "No assets found";
  return hasHeldContext
    ? "The account holds no assets"
    : "Type at least 2 characters to search";
};

const useAssetSearch = (heldByAccountId?: number) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearchTerm.trim();
  const hasSearch = trimmedSearch.length >= MIN_SEARCH_LENGTH;

  // With an account context the dropdown is useful before any typing: it
  // opens with the assets the account currently holds.
  const heldQuery = useListAssetsQuery(
    {
      held_by_account: heldByAccountId,
      held_only: true,
      page_size: ASSET_PAGE_SIZE,
    },
    { skip: !heldByAccountId },
  );

  // Typing searches ALL assets (held or not); held matches float to the top.
  const searchQuery = useListAssetsQuery(
    {
      search: trimmedSearch,
      page_size: ASSET_PAGE_SIZE,
      ...(heldByAccountId
        ? { held_by_account: heldByAccountId, ordering: "-held,symbol" }
        : {}),
    },
    { skip: !hasSearch },
  );

  const results = useMemo(() => {
    if (hasSearch) return searchQuery.data?.results ?? [];
    if (!heldByAccountId) return [];
    const held = heldQuery.data?.results ?? [];
    // 1 typed character: below the search threshold, narrow the held list
    // locally so the dropdown stays responsive.
    const needle = trimmedSearch.toLowerCase();
    if (!needle) return held;
    return held.filter(
      (asset) =>
        asset.symbol.toLowerCase().includes(needle) ||
        asset.name.toLowerCase().includes(needle),
    );
  }, [
    hasSearch,
    heldByAccountId,
    trimmedSearch,
    searchQuery.data,
    heldQuery.data,
  ]);

  const loading = hasSearch
    ? searchQuery.isLoading || searchQuery.isFetching
    : !!heldByAccountId && (heldQuery.isLoading || heldQuery.isFetching);

  return {
    searchTerm,
    setSearchTerm,
    trimmedSearch,
    results,
    loading,
    hasHeldContext: !!heldByAccountId,
  };
};

interface BaseAssetAutocompleteProps {
  label?: string;
  placeholder?: string;
  // When set, assets this account holds are listed first and highlighted.
  heldByAccountId?: number;
}

const HELD_OPTION_BG = "rgba(46, 125, 50, 0.12)";

interface SingleAssetAutocompleteProps extends BaseAssetAutocompleteProps {
  multiple?: false;
  value: number | "";
  onChange: (assetId: number | "") => void;
  required?: boolean;
}

interface MultiAssetAutocompleteProps extends BaseAssetAutocompleteProps {
  multiple: true;
  value: SelectedAsset[];
  onChange: (assets: SelectedAsset[]) => void;
}

type AssetAutocompleteProps =
  | SingleAssetAutocompleteProps
  | MultiAssetAutocompleteProps;

const LoadingAdornment = ({ loading }: { loading: boolean }) =>
  loading ? <CircularProgress color="inherit" size={20} /> : null;

function SingleAssetAutocomplete({
  value,
  onChange,
  label = DEFAULT_LABEL,
  placeholder = DEFAULT_PLACEHOLDER,
  required = false,
  heldByAccountId,
}: SingleAssetAutocompleteProps) {
  const {
    searchTerm,
    setSearchTerm,
    trimmedSearch,
    results,
    loading,
    hasHeldContext,
  } = useAssetSearch(heldByAccountId);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const options = useMemo(() => {
    if (selectedAsset && !results.some((a) => a.id === selectedAsset.id)) {
      return [selectedAsset, ...results];
    }
    return results;
  }, [results, selectedAsset]);

  useEffect(() => {
    if (value === "") {
      setSelectedAsset(null);
      setSearchTerm("");
    }
  }, [value, setSearchTerm]);

  const clearSelection = () => {
    setSelectedAsset(null);
    setSearchTerm("");
    onChange("");
  };

  return (
    <Autocomplete
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={getAssetLabel}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      value={selectedAsset}
      loading={loading}
      onInputChange={(_, newValue, reason) => {
        if (reason === "input") {
          setSearchTerm(newValue);
          if (selectedAsset) clearSelection();
        } else if (reason === "clear") {
          clearSelection();
        }
      }}
      onChange={(_, asset) => {
        setSelectedAsset(asset);
        onChange(asset?.id ?? "");
        if (asset) setSearchTerm(getAssetLabel(asset));
      }}
      renderOption={(optionProps, option) => (
        <li
          {...optionProps}
          key={option.id}
          style={{
            ...optionProps.style,
            backgroundColor: option.held ? HELD_OPTION_BG : undefined,
          }}
        >
          {getAssetLabel(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                <LoadingAdornment loading={loading} />
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={getNoOptionsText(trimmedSearch, hasHeldContext)}
    />
  );
}

function MultiAssetAutocomplete({
  value,
  onChange,
  label = DEFAULT_LABEL,
  placeholder = DEFAULT_PLACEHOLDER,
  heldByAccountId,
}: MultiAssetAutocompleteProps) {
  const {
    searchTerm,
    setSearchTerm,
    trimmedSearch,
    results,
    loading,
    hasHeldContext,
  } = useAssetSearch(heldByAccountId);

  const options = useMemo<SelectedAsset[]>(() => {
    const resultsAsSelected: SelectedAsset[] = results.map(toSelectedAsset);
    const resultIds = new Set(resultsAsSelected.map((r) => r.id));
    const missing = value.filter((v) => !resultIds.has(v.id));
    return [...missing, ...resultsAsSelected];
  }, [results, value]);

  const normalize = (assets: SelectedAsset[]): SelectedAsset[] =>
    assets.map(({ id, symbol, name }) => ({ id, symbol, name }));

  return (
    <Autocomplete<SelectedAsset, true>
      multiple
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={getAssetLabel}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      value={value}
      inputValue={searchTerm}
      loading={loading}
      onInputChange={(_, next, reason) => {
        if (reason === "input") setSearchTerm(next);
        else if (reason === "clear" || reason === "reset") setSearchTerm("");
      }}
      onChange={(_, assets) => {
        onChange(normalize(assets));
        setSearchTerm("");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                <LoadingAdornment loading={loading} />
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={getNoOptionsText(trimmedSearch, hasHeldContext)}
    />
  );
}

export default function AssetAutocomplete(props: AssetAutocompleteProps) {
  return props.multiple ? (
    <MultiAssetAutocomplete {...props} />
  ) : (
    <SingleAssetAutocomplete {...props} />
  );
}
